import { IComponentOptions, IPromise, IQService } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import equal from "fast-deep-equal";
import { GtmhubController, IRestLayerRequest } from "@gtmhub/core";
import { IModalComponent } from "@gtmhub/core/routing";
import { IIndicator } from "@gtmhub/error-handling";
import { OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { IAllGoal, IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { goalIsNeitherObfuscatedNorLocked } from "@gtmhub/goals/utils";
import { localize } from "@gtmhub/localization";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { filterActiveSessionsWithoutCheckDate } from "@gtmhub/sessions/redux/session-selectors";
import { Ng1UiModalService } from "@gtmhub/shared/components/modal/ng1-modal.service";
import { INgRedux } from "@gtmhub/state-management";
import { IdMap } from "@gtmhub/util";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { Session } from "@webapp/sessions/models/sessions.model";
import { MetricLinkService } from "../../services/metric-link.service";
import { MetricService } from "../../services/metric.service";
import { calculateContributorMetricValues, createContributorMetric } from "../../utils";
import { ContributingGoal, ICascadingMetricContributor } from "./cascading-metric-contributor.component";

export interface ITotalContribution {
  totalValue: number;
  totalPercentage: number;
}

interface ICascadingMetricFormComponentBindingsResolve {
  cascadingMetric: Metric;
  goalName: string;
}

interface ICascadingMetricFormComponentRedux {
  sessionIds: string[];
  sessionsMap: IdMap<Session>;
}

export const incrementNewlyCreatedMetricsByGoalMap = (goalsMap: IdMap<number>, goalId: string) => {
  if (goalsMap[goalId]) {
    goalsMap[goalId]++;
  } else {
    goalsMap[goalId] = 1;
  }
};

const bindStateToCtrl = (state: ISessionsStoreState): ICascadingMetricFormComponentRedux => {
  return {
    sessionIds: filterActiveSessionsWithoutCheckDate(state.sessions.items).map((session) => session.id),
    sessionsMap: state.sessions.map,
  };
};

export const metricsFieldsInCascadingMetricForm = "id,name,ownerIds,initialValue,actual,target,goalId,accountId,format";
export const goalsFieldsInCascadingMetricForm =
  "metrics{id,name,attainment,ownerIds,manualType,goalId,customFields,initialValue,target,format,links{spec},cascadeType,sourceMetricId,targetMetricIds}," +
  "ownerIds,assignee{name,email,color,avatar,picture,type}," +
  "name,parentId,childrenCount,fullSubTreeCount,metricsCount,attainment,attainmentTypeString,fullAggregatedAttainment,sessionId," +
  "obfuscated,private,parentGoalSessionId,locked,closedStatus," +
  "workflow{status,reviewersNeeded,reviewers,approvedAt}";
const attainmentTypeFilter = { $or: [{ attainmentType: { $exists: false } }, { attainmentType: { $in: ["average_kr", "average_children_kr"] } }] };
const okrStatusFilter = { "closedStatus.status": { $nin: ["abandoned"] } };

/* eslint-disable @typescript-eslint/no-empty-interface */
export interface ICascadingMetricFormComponentBindings extends IModalComponent<ICascadingMetricFormComponentBindingsResolve> {}
export interface CascadingMetricFormCtrl extends ICascadingMetricFormComponentRedux, ICascadingMetricFormComponentBindings {}
export class CascadingMetricFormCtrl extends GtmhubController {
  indicators: {
    cascading?: IIndicator;
    loading?: IIndicator;
  };
  cascadingMetric: Metric;
  contributors: ICascadingMetricContributor[] = [];
  totalContribution: ITotalContribution;
  isEditCascading: boolean;
  alreadySelectedMetricIds: string[] = [];
  newlyCreatedMetricsByGoalMap: IdMap<number> = {};
  childGoals: IGoal[];
  sessionGoals: IGoal[];
  hasErrorInForm: boolean;
  includeInitialValues: boolean;

  static $inject = ["$q", "PlanningSessionsActions", "$ngRedux", "GoalService", "MetricService", "MetricLinkService", "OkrMethodologyFacade", "Ng1UiModalService"];

  constructor(
    private $q: IQService,
    planningSessionsActions: PlanningSessionsActions,
    $ngRedux: INgRedux,
    private goalService: GoalService,
    private metricService: MetricService,
    private metricLinkService: MetricLinkService,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private ng1UiModalService: Ng1UiModalService
  ) {
    super();
    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(planningSessionsActions.getSessionsIfMissing());
    this.indicators = {};
  }

  $onInit(): void {
    this.indicators.loading = { progress: true };

    this.cascadingMetric = angular.copy(this.resolve.cascadingMetric);
    this.isEditCascading = !!this.cascadingMetric.cascadeType;

    if (!this.cascadingMetric.cascadeType) {
      this.cascadingMetric.cascadeType = "delta_value";
    }
    this.includeInitialValues = this.cascadingMetric.cascadeType === "absolute_value";

    const initPromises: IPromise<void>[] = [this.getChildrenGoals()];
    if (this.isEditCascading) initPromises.push(this.getCascadingMetrics());

    this.$q
      .all(initPromises)
      .then(() => {
        delete this.indicators.loading;
        this.calculateContributionValues();
        this.getSessionGoals();
      })
      .catch((error) => (this.indicators.loading = { error }));
  }

  addNewRow(): void {
    this.contributors.push({});
  }

  deleteRow(index: number): void {
    this.contributors.splice(index, 1);
    this.calculateContributionValues();
  }

  includeInitialValuesChange(include: boolean): void {
    this.cascadingMetric.cascadeType = include ? "absolute_value" : "delta_value";
    this.calculateContributionValues();
  }

  calculateContributionValues(index?: number): void {
    if (typeof index === "number") {
      this.calculateSingleContributionPercentage(index);
    } else {
      this.calculateEveryContributionPercentage();
    }

    this.totalContribution = this.calculateTotalContribution();
    this.hasErrorInForm =
      this.cascadingMetric.initialValue >= this.cascadingMetric.target ||
      this.contributors.some((contributor) => contributor.metric && contributor.metric.initialValue >= contributor.metric.target);
  }

  cascadeKr(): void {
    this.indicators.cascading = {
      progress: true,
    };

    const metricPromises: IPromise<Metric>[] = this.getMetricPromises();

    this.$q
      .all(metricPromises)
      .then((metrics) => {
        const targetMetricIds: string[] = metrics.map((metric) => metric.id);
        const metricIdsToUnlink: string[] = this.isEditCascading
          ? this.cascadingMetric.links.spec.cascading.map((metricLink) => metricLink.id).filter((metricId) => !metrics.some((metric) => metric.id === metricId))
          : [];

        if (!targetMetricIds.length && !metricIdsToUnlink.length) {
          this.close();
          return;
        }

        const metricIdsToLink: string[] = this.isEditCascading
          ? targetMetricIds.filter((targetId) => !this.cascadingMetric.links.spec.cascading.some((metricLink) => metricLink.id === targetId))
          : [...targetMetricIds];
        // if the cascading metric has a hard deadline, all the newly linked metrics should
        // have the same due date or else they will be changed to the cascading metric's due date
        const newMetricsHaveDifferentDueDate = !this.cascadingMetric.dueDate
          ? false
          : metrics.filter((metric) => metricIdsToLink.includes(metric.id)).some((metric) => metric.dueDate && metric.dueDate !== this.cascadingMetric.dueDate);

        if (newMetricsHaveDifferentDueDate) {
          const deferred = this.$q.defer();

          this.ng1UiModalService.confirm({
            uiTitle: localize("key_results_with_deadlines"),
            uiContent: localize("key_results_with_deadlines_info"),
            uiIconType: null,
            uiOkText: localize("remove_deadlines"),
            uiOkDanger: true,
            uiOnOk: () => {
              this.indicators = {};
              deferred.resolve(this.handleLinkPromise(metricIdsToUnlink, metricIdsToLink, targetMetricIds, newMetricsHaveDifferentDueDate));
            },
            uiCancelText: localize("cancel"),
            uiOnCancel: () => {
              this.indicators = {};
            },
          });
          return deferred.promise;
        } else {
          return this.handleLinkPromise(metricIdsToUnlink, metricIdsToLink, targetMetricIds, newMetricsHaveDifferentDueDate);
        }
      })
      .catch((error) => {
        this.indicators.cascading = { error };
      });
  }

  handleLinkPromise(metricIdsToUnlink, metricIdsToLink, targetMetricIds, newMetricsHaveDifferentDueDate): IPromise<void> {
    let linkPromise = this.$q.resolve();
    if (this.isEditCascading) {
      if (metricIdsToUnlink.length || metricIdsToLink.length) {
        linkPromise = this.patchLink(metricIdsToLink, metricIdsToUnlink, { shouldSkipDueDatesUpdates: newMetricsHaveDifferentDueDate });
      }
    } else {
      linkPromise = this.postLink(targetMetricIds, { shouldSkipDueDatesUpdates: newMetricsHaveDifferentDueDate });
    }
    return linkPromise.then(() => {
      if (!equal(this.cascadingMetric, this.resolve.cascadingMetric)) {
        this.updateCascadingMetricCascadeType();
      }
      this.close();
    });
  }

  focusSearch({ isSearchFocused, index }: { isSearchFocused: boolean; index: number }): void {
    this.contributors = this.contributors.map((contributor, i) => ({
      ...contributor,
      isSearchFocused: i === index ? isSearchFocused : false,
    }));
  }

  closeSearchMenu(event: PointerEvent, index: number): void {
    if (event.target) {
      this.focusSearch({ isSearchFocused: false, index });
    }
  }

  private getMetricPromises(): IPromise<Metric>[] {
    return this.contributors
      .filter((contributor) => contributor.metric)
      .map((contributor) =>
        contributor.metric.id
          ? this.updateExistingMetric(
              contributor.goal.metrics.find((metric) => metric.id === contributor.metric.id),
              contributor.metric
            )
          : this.metricService.createMetric(contributor.metric)
      );
  }

  private patchLink(metricIdsToLink: string[], metricIdsToUnlink: string[], options: { shouldSkipDueDatesUpdates: boolean }): IPromise<void> {
    return this.metricLinkService.patchLinks({
      goalId: this.cascadingMetric.goalId,
      sourceMetricId: this.cascadingMetric.id,
      linkType: "cascading",
      entityIdsToLink: metricIdsToLink,
      entityIdsToUnlink: metricIdsToUnlink,
      shouldSkipDueDatesUpdates: options.shouldSkipDueDatesUpdates,
    });
  }

  private postLink(targetMetricIds: string[], options: { shouldSkipDueDatesUpdates: boolean }): IPromise<void> {
    return this.metricLinkService.bulkLink({
      goalId: this.cascadingMetric.goalId,
      sourceMetricId: this.cascadingMetric.id,
      linkType: "cascading",
      targetMetricIds: targetMetricIds,
      shouldSkipDueDatesUpdates: options.shouldSkipDueDatesUpdates,
    });
  }

  private calculateSingleContributionPercentage(index: number): void {
    const contributorMetric = this.contributors[index].metric;
    this.contributors[index].contributionPercentage = calculateContributorMetricValues(this.cascadingMetric, contributorMetric).percentage;
  }

  private calculateEveryContributionPercentage(): void {
    this.contributors.forEach((contributor) => {
      contributor.contributionPercentage = calculateContributorMetricValues(this.cascadingMetric, contributor.metric).percentage;
    });
  }

  private calculateTotalContribution(): ITotalContribution {
    return this.contributors.reduce(
      (totalContribution, contributor) => {
        totalContribution.totalPercentage += contributor.contributionPercentage || 0;
        totalContribution.totalValue += calculateContributorMetricValues(this.cascadingMetric, contributor.metric).value;

        return totalContribution;
      },
      { totalValue: 0, totalPercentage: 0 }
    );
  }

  private getChildrenGoals(): IPromise<void> {
    const restLayerReq: IRestLayerRequest = {
      filter: {
        $and: [{ parentId: this.cascadingMetric.goalId }, { sessionId: { $in: this.sessionIds } }, attainmentTypeFilter, okrStatusFilter],
      },
      fields: goalsFieldsInCascadingMetricForm,
    };

    return this.goalService.getGoalsV2<IAllGoal>(restLayerReq).then((res) => {
      this.childGoals = (res.items as IGoal[]).filter(goalIsNeitherObfuscatedNorLocked);

      if (!this.isEditCascading && this.childGoals.length) {
        this.prefillContributors();
      }
    });
  }

  private getSessionGoals(): void {
    const restLayerReq: IRestLayerRequest = {
      filter: {
        $and: [{ sessionId: this.cascadingMetric.sessionId }, { _id: { $ne: this.cascadingMetric.goalId } }, attainmentTypeFilter, okrStatusFilter],
      },
      fields: goalsFieldsInCascadingMetricForm,
    };

    this.goalService.getGoalsV2<IAllGoal>(restLayerReq).then((res) => {
      this.sessionGoals = (res.items as IGoal[]).filter(goalIsNeitherObfuscatedNorLocked);
    });
  }

  private getCascadingMetrics(): IPromise<void> {
    const cascadingMetricsIds = this.cascadingMetric.links?.spec?.cascading?.map((cascading) => cascading.id) || [];

    return this.metricService.getMetricsV2({ filter: { _id: { $in: cascadingMetricsIds } }, fields: metricsFieldsInCascadingMetricForm }).then((metrics) => {
      const goalIds = metrics.items.map((metric) => metric.goalId);

      return this.goalService.getGoalsV2({ filter: { _id: { $in: goalIds } }, fields: `id,name,metrics{${metricsFieldsInCascadingMetricForm}}` }).then((goals) => {
        metrics.items.forEach((metric, index) => {
          this.addNewRow();
          const goal = goals.items.find((goal) => goal.id === metric.goalId);
          this.contributors[index].goal = goal;
          this.contributors[index].metric = metric;
        });
      });
    });
  }

  private prefillContributors(): void {
    this.childGoals
      .filter((goal) => {
        const goalSessionSettings = this.sessionsMap[goal.sessionId].settings;
        const limitMetricsPerGoal = this.okrMethodologyFacade.getLimitSetting("metric", goalSessionSettings);
        const currentMetricsCount = goal.metricsCount || 0;
        const newlyCreatedMetrics = this.newlyCreatedMetricsByGoalMap[goal.id] || 0;

        return currentMetricsCount + newlyCreatedMetrics < limitMetricsPerGoal;
      })
      .forEach((goal, index, arr) => {
        this.addNewRow();
        this.contributors[index].goal = <ContributingGoal>goal;
        this.contributors[index].metric = createContributorMetric(this.cascadingMetric, goal.id);
        this.contributors[index].metric.target = Math.round(this.cascadingMetric.target / arr.length);
        incrementNewlyCreatedMetricsByGoalMap(this.newlyCreatedMetricsByGoalMap, goal.id);
      });
  }

  private updateExistingMetric(originalMetric: Metric, modifiedMetric: Metric): IPromise<Metric> {
    delete originalMetric.color;
    delete modifiedMetric.color;

    if (equal(originalMetric, modifiedMetric)) {
      return this.$q.resolve(<Metric>{ id: modifiedMetric.id });
    }

    return this.metricService.patchMetric({
      id: modifiedMetric.id,
      name: modifiedMetric.name,
      ownerIds: modifiedMetric.ownerIds,
      initialValue: modifiedMetric.initialValue,
      actual: modifiedMetric.actual,
      target: modifiedMetric.target,
    });
  }

  private updateCascadingMetricCascadeType(): IPromise<Metric> {
    return this.metricService.patchMetric({
      id: this.cascadingMetric.id,
      cascadeType: this.cascadingMetric.cascadeType,
      initialValue: this.cascadingMetric.initialValue,
      actual: this.cascadingMetric.actual,
      target: this.cascadingMetric.target,
    });
  }
}

export class CascadingMetricFormModal {
  static $inject = ["$uibModal", "Ng1UiModalService"];

  constructor(private $uibModal: IModalService, private modalService: Ng1UiModalService) {}

  handleOpenCascadingForm(metric: Metric, goalName: string): void {
    if (metric.dynamic && !metric.cascadeType.length) {
      this.openDynamicMetricWarningModal(metric, goalName);
    } else {
      this.openCascadeMetricForm(metric, goalName);
    }
  }

  handleOpenCascadingFormFromContributor(input: { metric: Metric; goalName: string }): void {
    this.handleOpenCascadingForm(input.metric, input.goalName);
  }

  private openCascadeMetricForm = (cascadingMetric: Metric, goalName: string): void => {
    this.$uibModal.open({
      component: "cascadingMetricForm",
      resolve: {
        cascadingMetric: () => cascadingMetric,
        goalName: () => goalName,
      },
      windowClass: "w-1175px no-min-height",
      backdropClass: "dark-backdrop",
      backdrop: true,
    });
  };

  private openDynamicMetricWarningModal(metric: Metric, goalName: string): void {
    this.modalService.confirm({
      uiTitle: localize("this_key_result_is_dynamic"),
      uiContent: localize("break_dynamic_kr_warning"),
      uiOkText: localize("confirm_cascade"),
      uiOnOk: () => this.openCascadeMetricForm(metric, goalName),
      uiCancelText: localize("cancel"),
    });
  }
}

export const CascadingMetricFormComponent: IComponentOptions = {
  bindings: {
    resolve: "<",
    close: "&",
    dismiss: "&",
  },
  controller: CascadingMetricFormCtrl,
  template: require("./cascading-metric-form.html"),
};
